import { useState } from "react";
import { Loader2, Minus, Plus, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export function ReservationDialog({ event, open, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", quantity: 1, notes: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  if (!event) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const max = event.available || 1;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/reservations", {
        ...form, event_id: event.id, quantity: Number(form.quantity), origin: window.location.origin,
      });
      if (data.payment_required && data.redsys) {
        const f = document.createElement("form");
        f.method = "POST";
        f.action = data.redsys.action;
        Object.entries(data.redsys.fields).forEach(([k, v]) => {
          const i = document.createElement("input");
          i.type = "hidden"; i.name = k; i.value = v; f.appendChild(i);
        });
        document.body.appendChild(f);
        f.submit();
        return;
      }
      setDone(true);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo completar la reserva");
    } finally {
      setLoading(false);
    }
  };

  const close = () => { setDone(false); setForm({ name: "", email: "", phone: "", quantity: 1, notes: "" }); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-md">
        {done ? (
          <div className="py-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#DCFCE7] text-[#16A34A]"><PartyPopper className="h-8 w-8" /></div>
            <h3 className="font-display text-2xl font-bold text-[#1C1917]">¡Reserva confirmada!</h3>
            <p className="mt-2 text-[#57534E]">Te hemos enviado un email de confirmación a <b>{form.email}</b>. ¡Te esperamos!</p>
            <button data-testid="reservation-close-button" onClick={close} className="mt-6 rounded-full bg-[#0369A1] px-8 py-3 font-bold text-white hover:bg-[#075985]">Perfecto</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Reservar entradas</DialogTitle>
            </DialogHeader>
            <p className="-mt-1 text-sm text-[#57534E]">{event.name}{event.price ? ` · ${formatPrice(event.price)} / entrada` : ""}</p>
            <form onSubmit={submit} className="mt-3 space-y-4">
              <div><Label className="font-semibold">Nombre</Label>
                <Input data-testid="reservation-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5" /></div>
              <div><Label className="font-semibold">Email</Label>
                <Input data-testid="reservation-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5" /></div>
              <div><Label className="font-semibold">Teléfono</Label>
                <Input data-testid="reservation-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} required placeholder="+34 600 000 000" className="mt-1.5" /></div>
              <div>
                <Label className="font-semibold">Nº de personas</Label>
                <div className="mt-1.5 flex items-center gap-3">
                  <button type="button" onClick={() => set("quantity", Math.max(1, form.quantity - 1))} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-[#57534E] hover:bg-[#E0F2FE]"><Minus className="h-4 w-4" /></button>
                  <span data-testid="reservation-quantity" className="w-10 text-center text-lg font-bold">{form.quantity}</span>
                  <button type="button" onClick={() => set("quantity", Math.min(max, form.quantity + 1))} className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-[#57534E] hover:bg-[#E0F2FE]"><Plus className="h-4 w-4" /></button>
                  <span className="text-sm text-[#A8A29E]">({max} disponibles)</span>
                </div>
              </div>
              <div><Label className="font-semibold">Notas (opcional)</Label>
                <Textarea data-testid="reservation-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-1.5" placeholder="Alergias, preferencias…" /></div>
              {event.price ? <div className="rounded-xl bg-secondary px-4 py-3 text-center font-bold text-[#075985]">Total: {formatPrice(event.price * form.quantity)}</div> : null}
              <button data-testid="reservation-submit-button" disabled={loading}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#EA580C] font-bold text-white transition-all hover:bg-[#C2410C] disabled:opacity-60">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (event.price ? `Ir a pagar (${formatPrice(event.price * form.quantity)})` : "Confirmar reserva")}
              </button>
              {event.price ? <p className="text-center text-xs text-[#A8A29E]">Pago seguro con tarjeta a través de Redsys.</p> : null}
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
